// =============================================================================
// lib/api/index.ts -- Patch 010.A.4 R7. Composes the per-domain method
// objects into the single `api` surface consumed by the rest of the
// frontend. Re-exports the types + request helpers so consumers continue
// to import everything from `'../lib/api'` unchanged.
//
// 010.A.4.0.1 CI fix (2026-05-14): re-triggers Pages auto-deploy after
// the 67bf9a7 deploy failed at Cloudflare's API with "Invalid commit
// message" -- root cause was U+2713 check mark in the prior commit
// message body, combined with 3080-byte message length. Cloudflare's
// validation is stricter than the error wording suggests. ASCII-only
// commit messages going forward for any push that triggers Pages.
// =============================================================================

import { request } from './request'
import { askApi } from './ask'
import { autopopApi } from './autopop'
import { candidatesApi } from './candidates'
import { catalogApi } from './catalog'
import { bidsApi } from './bids'
import { budgetApi } from './budget'
import { rosterApi } from './roster'
import { scsImportApi } from './scsImport'
import { changeOrdersApi } from './changeOrders'
import { clientCommsApi } from './clientComms'
import { drawsApi } from './draws'
import { decisionsApi } from './decisions'
import { documentsApi } from './documents'
import { drawSetupApi } from './drawSetup'
import { changeOrderSetupApi } from './changeOrderSetup'
import { dailyLogsApi } from './dailyLogs'
import { drawQueueApi } from './drawQueue'
import { drawCompileApi } from './drawCompile'
import { settingsApi } from './settings'
import { complianceApi } from './compliance'
import { materialsApi } from './materials'
import { digestsApi } from './digests'
import { insightsApi } from './insights'
import { factsApi } from './facts'
import { generatedDocumentsApi } from './generatedDocuments'
import { inspectionsApi } from './inspections'
import { integrityApi } from './integrity'
import { laneBApi } from './laneB'
import { notesApi } from './notes'
import { planSetsApi } from './planSets'
import { publishedContentApi } from './publishedContent'
import { websiteLeadsApi } from './websiteLeads'
import { marketingApi } from './marketing'
import { emailApi } from './email'
import { campaignsApi } from './campaigns'
import { lotProspectsApi } from './lotProspects'
import { leadScoresApi } from './leadScores'
import { gbpApi } from './gbp'
import { reactivationApi } from './reactivation'
import { partnerNetworkApi } from './partnerNetwork'
import { crmApi } from './crm'
import { testimonialsApi } from './testimonials'
import { profilesApi } from './profiles'
import { projectsApi } from './projects'
import { punchApi } from './punch'
import { planReviewApi } from './planReview'
import { scheduleApi } from './schedule'
import { masterScheduleApi } from './masterSchedule'
import { scopeCascadeApi } from './scopeCascade'
import { contractualScopeApi } from './contractualScope'
import { selectionsApi } from './selections'
import { siteWalksApi } from './siteWalks'
import { subsApi } from './subs'
import { contractorsApi } from './contractors'
import { specBookApi } from './specBook'
import { w9Api } from './w9'
import { welcomeHomeApi } from './welcomeHome'
import { wizardApi } from './wizard'
import type { CommandBoardResponse, HealthResponse, MeResponse } from './types'

export const api = {
  health: () => request<HealthResponse>('/api/health'),
  me: () => request<MeResponse>('/api/me'),
  commandBoard: (projectId: string) =>
    request<CommandBoardResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/command-board`,
    ),
  schedule: scheduleApi,
  masterSchedule: masterScheduleApi,
  laneB: laneBApi,
  projects: projectsApi,
  autopop: autopopApi,
  facts: factsApi,
  documents: documentsApi,
  generatedDocuments: generatedDocumentsApi,
  planSets: planSetsApi,
  planReview: planReviewApi,
  profiles: profilesApi,
  candidates: candidatesApi,
  catalog: catalogApi,
  clientComms: clientCommsApi,
  scopeCascade: scopeCascadeApi,
  contractualScope: contractualScopeApi,
  selections: selectionsApi,
  siteWalks: siteWalksApi,
  specBook: specBookApi,
  subs: subsApi,
  contractors: contractorsApi,
  wizard: wizardApi,
  budget: budgetApi,
  scsImport: scsImportApi,
  bids: bidsApi,
  roster: rosterApi,
  changeOrders: changeOrdersApi,
  draws: drawsApi,
  punch: punchApi,
  notes: notesApi,
  w9: w9Api,
  decisions: decisionsApi,
  publishedContent: publishedContentApi,
  websiteLeads: websiteLeadsApi,
  marketing: marketingApi,
  email: emailApi,
  campaigns: campaignsApi,
  lotProspects: lotProspectsApi,
  leadScores: leadScoresApi,
  gbp: gbpApi,
  reactivation: reactivationApi,
  partnerNetwork: partnerNetworkApi,
  crm: crmApi,
  testimonials: testimonialsApi,
  welcomeHome: welcomeHomeApi,
  ask: askApi,
  drawSetup: drawSetupApi,
  changeOrderSetup: changeOrderSetupApi,
  dailyLogs: dailyLogsApi,
  drawQueue: drawQueueApi,
  drawCompile: drawCompileApi,
  settings: settingsApi,
  compliance: complianceApi,
  materials: materialsApi,
  digests: digestsApi,
  insights: insightsApi,
  inspections: inspectionsApi,
  integrity: integrityApi,
}

// Re-export the public surface so consumers continue to import everything
// from `'../lib/api'` (barrel pattern).
export { ApiError, BASE } from './request'
export * from './types'
export type {
  SpecBookResponse,
  SpecSection,
  SpecSubsection,
  SpecSubsectionPatch,
} from './specBook'
export type {
  CreateSelectionInput,
  PatchSelectionInput,
  SelectionListResponse,
  SelectionRow,
  SelectionSummary,
} from './selections'
export type { FactConsumer, FactInsightsResponse, FactContradiction } from './facts'
export type {
  ContractualScopeApproveResult,
  ContractualScopeItemRow,
  ContractualScopeListResponse,
} from './contractualScope'
export type {
  ChangeOrderRow,
  ChangeOrderListResponse,
  ChangeOrderStatus,
  CreateChangeOrderInput,
  PatchChangeOrderInput,
} from './changeOrders'
export type { DrawRequestRow, DrawListResponse, CreateDrawInput } from './draws'
export type {
  BidPackageRow,
  BidPackageListResponse,
  LevelingBid,
  LevelingPackage,
  LevelingResponse,
  CreatePackageInput,
  AddBidInput,
  AwardResponse,
  ReviewBidInput,
} from './bids'
export type {
  BudgetLineRow,
  BudgetLinesResponse,
  CreateBudgetLineInput,
} from './budget'
export type { RosterMember, ProjectRosterResponse } from './roster'
export type {
  CoiBackfillSummary,
  SubRosterEntry,
  SubsRosterResponse,
} from './subs'
export type {
  ContractorListRow,
  ContractorAlias,
  ContractorDetail,
  ContractorListResponse,
  ContractorDetailResponse,
  AddAliasResponse,
  ContractorMergeResponse,
  ContractorRating,
  ContractorCoiRecord,
  ContractorW9Record,
  ContractorPatch,
  RatingInput,
  RequestW9Response,
  RequestCoiResponse,
} from './contractors'
export type {
  PunchItemRow,
  PunchListResponse,
  PunchStatus,
  CreatePunchInput,
  PatchPunchInput,
} from './punch'
export type {
  NoteRow,
  NoteListResponse,
  NoteCategory,
  NotePriority,
  NotesClassifyResponse,
} from './notes'
export type {
  WorkItemRollupRow,
  WorkItemRollupResponse,
  CatalogPhase,
  CatalogPhasesResponse,
} from './catalog'
export type {
  WelcomeHomeItem,
  WelcomeHomeItemGroup,
  WelcomeHomeGroupCount,
  WelcomeHomeSummary,
  WelcomeHomeListResponse,
  SeedWelcomeHomeResponse,
  CreateWelcomeHomeItemInput,
  PatchWelcomeHomeItemInput,
  WelcomeHomeCadenceResponse,
} from './welcomeHome'
export type {
  AskCitation,
  AskAnswerResponse,
  AskConversationSummary,
  AskMessage,
  AskConversationDetail,
  AskConversationListResponse,
} from './ask'
export type {
  DrawParty,
  DrawPartyKind,
  DrawTemplate,
  DrawPartyInput,
  PatchDrawPartyInput,
} from './drawSetup'
export type {
  DrawReviewState,
  DrawQueueInvoice,
  DrawQueueResponse,
  DrawInvoiceReceipt,
  DrawInvoiceDetailResponse,
  DrawLienWaiver,
  DrawIntakeResult,
  PatchDrawInvoiceInput,
  CreateLienWaiverInput,
  ScsLineLite,
} from './drawQueue'
export type {
  CompileResult,
  ConfirmPaymentResult,
  CloseoutReadiness,
  GcFeeDraw,
  GcFeeResponse,
} from './drawCompile'
export type {
  IntegrationsStatus,
  OrgSetting,
  ProjectSettings,
} from './settings'
export type {
  ComplianceCategory,
  ComplianceRecurrence,
  ComplianceEvent,
  ComplianceListResponse,
  CreateComplianceInput,
  ComplianceAlert,
  ComplianceAlertsResponse,
} from './compliance'
export type {
  MaterialStatus,
  MaterialRow,
  MaterialListResponse,
  CreateMaterialInput,
  PatchMaterialInput,
} from './materials'
export type { DigestStatus, DigestRow } from './digests'
export type {
  InsightKind,
  InsightReviewState,
  InsightRow,
} from './insights'
export type {
  InspectionPrepChecklist,
  InspectionGate,
  InspectionListResponse,
  GeneratePrepResult,
} from './inspections'
export type {
  IntegrityFinding,
  FindingsResponse,
  IntegrityRun,
  RunResponse,
  FindingSeverity,
  FindingDisposition,
  LearnedCheck,
} from './integrity'
